import React, { useState, useEffect } from "react";
import CreateProjectButton from "../CreateProjectButton/CreateProjectButton";

const Roadmap = ({ roadmaps, submissions = [], onCreateRoadmap, isAdmin }) => {
  const [collapsed, setCollapsed] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);
  const [now, setNow] = useState(new Date());
  const [activeCheckpointId, setActiveCheckpointId] = useState(null);
  const [progressData, setProgressData] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Convert any object map into array
  const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return Object.values(val);
  };

  // Flatten all checkpoints from all phases
  const allCheckpoints = roadmaps.flatMap((phase) =>
    toArray(phase.checkpoints || []).map((cp) => ({
      ...cp,
      phase: phase.title,
    }))
  );

  // Find the single active checkpoint across all phases
  useEffect(() => {
    if (allCheckpoints.length === 0) return;

    let active = null;
    const sorted = [...allCheckpoints].sort(
      (a, b) => new Date(a.startDate) - new Date(b.startDate)
    );

    for (let i = 0; i < sorted.length; i++) {
      const start = new Date(sorted[i].startDate);
      const end = new Date(sorted[i].endDate);
      if (now >= start && now <= end) {
        active = sorted[i];
        break;
      }
    }

    // If none are active, find next upcoming
    if (!active) {
      active = sorted.find((cp) => new Date(cp.startDate) > now) || sorted.at(-1);
    }

    setActiveCheckpointId(active.text); // using text as ID substitute
  }, [now, allCheckpoints]);

  const phaseOrder = ["Forge", "Build", "Ship"];
  const columnWidth = 180;
  const gap = 24;
  const allWeeks = roadmaps.flatMap((p) =>
    toArray(p.checkpoints).map((c) => c.week)
  );
  const minWeek = Math.min(...allWeeks);

  // --- PROGRESS CALCULATION LOGIC ---
  const calculateProgress = (roadmaps, submissions) => {
    return roadmaps.map((phase) => {
      const checkpoints = toArray(phase.checkpoints).map((cp) => {
        // Match submissions by phase + checkpointIndex
        const cpSubs = submissions.filter(
          (s) => s.phase === phase.title && s.checkpointIndex === cp.index
        );

        const totalProgress = cpSubs.reduce(
          (sum, s) => sum + (s.progressDelta || 0),
          0
        );

        // Convert to percentage
        const progress = Math.min(1, totalProgress);

        return { ...cp, progress: progress * 100 };
      });

      // Group checkpoints by week
      const weeks = [...new Set(checkpoints.map((cp) => cp.week))].map((weekNum) => {
        const weekCPs = checkpoints.filter((cp) => cp.week === weekNum);
        const weekProgress = weekCPs.length
          ? weekCPs.reduce((sum, cp) => sum + cp.progress, 0) / weekCPs.length
          : 0;
        return { week: weekNum, progress: Math.round(weekProgress) };
      });

      // Average all weeks to get phase progress
      const phaseProgress = weeks.length
        ? Math.round(weeks.reduce((sum, w) => sum + w.progress, 0) / weeks.length)
        : 0;

      return { ...phase, checkpoints, weeks, progress: phaseProgress };
    });
  };


  useEffect(() => {
    if (roadmaps?.length && submissions?.length) {
      const updated = calculateProgress(roadmaps, submissions);
      setProgressData(updated);
    }
  }, [roadmaps, submissions]);

  const togglePhase = (phase) =>
    setCollapsed((prev) => ({ ...prev, [phase]: !prev[phase] }));

  if (!roadmaps || roadmaps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-10 bg-white rounded-2xl border border-zinc-200 shadow-sm transition-all duration-200">
        <p className="text-zinc-500 mb-4 text-sm">No roadmap has been created yet.</p>

        <button
          onClick={() => {
            if (!isAdmin) {
              // Inline feedback
              const toast = document.createElement("div");
              toast.className =
                "fixed bottom-6 right-6 bg-black text-white text-sm px-4 py-3 rounded-xl shadow-lg animate-fade-in-out z-[9999]";
              toast.innerText = "Only project admin can create a roadmap.";
              document.body.appendChild(toast);
              setTimeout(() => toast.remove(), 2800);
              return;
            }
            if (typeof onCreateRoadmap === "function") onCreateRoadmap();
          }}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
            isAdmin
              ? "bg-black text-white hover:bg-zinc-900 hover:scale-[1.02]"
              : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
          }`}
        >
          + Create Roadmap
        </button>
      </div>
    );
  }

  return (
    <div
      className={`w-full bg-white shadow-sm transition-all duration-500 overflow-hidden flex flex-col ${
        isExpanded ? "max-h-[1000px]" : "max-h-[80px]"
      }`}
    >
      {/* HEADER */}
      <div className="flex justify-end px-4 py-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-4 py-1 text-sm text-gray-500 hover:text-black transition"
        >
          {isExpanded ? "Hide Roadmap" : "Expand Roadmap"}
        </button>
      </div>

      {/* CONTENT */}
      {isExpanded && (
        <div className="relative p-4 overflow-x-auto snap-x snap-mandatory">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 h-full w-12 bg-gradient-to-r from-white to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>

          <div className="flex flex-col gap-16 min-w-max origin-top-left scale-90">
            {phaseOrder.map((phase) => {
              const phaseData = progressData.find((p) => p.title === phase);
              if (!phaseData) return null;

              const weeks = toArray(phaseData.checkpoints);
              if (!weeks.length) return null;

              const firstWeek = weeks[0].week;
              const totalWidth =
                weeks.length * columnWidth + (weeks.length - 1) * gap;
              const offset = (firstWeek - minWeek) * (columnWidth + gap);
              const isCollapsed = collapsed[phase];

              return (
                <div key={phase} className="flex flex-col items-start snap-start">
                  {/* Checkpoints row */}
                  {!isCollapsed && (
                    <div
                      className="flex gap-6 mb-6"
                      style={{ marginLeft: `${offset}px` }}
                    >
                      {weeks.map((cp, idx) => (
                        <WeekColumn
                          key={idx}
                          week={cp.week}
                          checkpoint={cp}
                          now={now}
                          isActive={cp.text === activeCheckpointId}
                        />
                      ))}
                    </div>
                  )}

                  {/* Phase Label */}
                  <button
                    onClick={() => togglePhase(phase)}
                    className="h-9 bg-black rounded-[40px] flex items-center pl-4 pr-3 cursor-pointer transition-all"
                    style={{
                      width: `${totalWidth}px`,
                      marginLeft: `${offset}px`,
                    }}
                  >
                    <span className="text-white text-sm font-semibold text-left flex-1">
                      {phase}
                    </span>
                    <span className="text-white ml-2">
                      {isCollapsed ? "▶" : "▲"}
                    </span>
                  </button>

                  {/* === Phase Progress Bar === */}
                  <div
                    className="flex items-center gap-2 mt-2 ml-[8px]"
                    style={{ marginLeft: `${offset}px` }}
                  >
                    <div
                      className="bg-gray-200 rounded-full h-2 overflow-hidden"
                      style={{ width: `${totalWidth}px` }}
                    >
                      <div
                        className="h-2 bg-purple-500 transition-all duration-700"
                        style={{ width: `${phaseData.progress || 0}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600">
                      {phaseData.progress ? `${phaseData.progress}%` : "0%"}
                    </span>
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper
const formatTimeLeft = (end, now) => {
  const diff = end - now;
  if (diff <= 0) return "Expired";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  return `${days}d ${hours}h ${mins}m left`;
};

// Render a single week card
const WeekColumn = ({ week, checkpoint, now, isActive }) => {
  const start = new Date(checkpoint.startDate);
  const end = new Date(checkpoint.endDate);
  const diff = end - now;
  const isExpired = diff <= 0; // expired condition
  const timeLeft =
    isExpired
      ? "Expired"
      : `${Math.floor(diff / (1000 * 60 * 60 * 24))}d ${Math.floor(
          (diff / (1000 * 60 * 60)) % 24
        )}h ${Math.floor((diff / (1000 * 60)) % 60)}m left`;

  return (
    <div className="flex flex-col items-start min-w-[180px] transition-all">
      {/* Week Header */}
      <div className="flex">
        <div className="w-[3px] bg-black mr-2" />
        <div className="flex flex-col text-left">
          <span className="text-black text-base font-bold">
            {String(week).padStart(2, "0")}
          </span>
          <span className="text-black text-base font-bold">Week</span>
        </div>
      </div>

      {/* === Weekly Progress Bar === */}
      <div className="flex items-center gap-2 mt-3">
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className="bg-blue-500 h-2 transition-all duration-500"
            style={{ width: `${checkpoint.progress || 0}%` }}
          />
        </div>
        <span className="text-xs font-medium text-gray-600">
          {checkpoint.progress ? `${checkpoint.progress}%` : "0%"}
        </span>
      </div>


      {/* Card */}
      <div
        className={`relative mt-2 p-3 w-52 rounded-lg border shadow-sm text-left transition-all duration-500 ${
          isActive
            ? "border-green-500 bg-green-50 scale-[1.03]"
            : isExpired
            ? "border-gray-300 bg-gray-100 opacity-60"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Active ping */}
        {isActive && (
          <div className="absolute -top-2 -right-2 flex items-center">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
          </div>
        )}

        {/* Cross-out line for expired */}
        {isExpired && (
          <div className="absolute left-0 top-1/2 w-full border-t border-gray-400 opacity-60 transform -translate-y-1/2"></div>
        )}

        {/* Countdown */}
        <p
          className={`text-xs font-semibold mb-1 ${
            isActive
              ? "text-green-600"
              : isExpired
              ? "text-gray-400 line-through"
              : "text-gray-400"
          }`}
        >
          {timeLeft}
        </p>

        {/* Text */}
        <p
          className={`text-sm font-medium mb-1 ${
            isExpired ? "text-gray-500 line-through" : "text-gray-800"
          }`}
        >
          {checkpoint.text}
        </p>

        <p
          className={`text-xs ${
            isExpired ? "text-gray-400 line-through" : "text-gray-400"
          }`}
        >
          {start.toLocaleDateString()} → {end.toLocaleDateString()}
        </p>
        <p
          className={`text-xs mt-1 ${
            isExpired ? "text-gray-400 line-through" : "text-gray-500"
          }`}
        >
          {checkpoint.hours} hrs
        </p>

        {/* === CP Progress Bar === */}
        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden mt-2">
          <div
            className="h-2 bg-green-500 transition-all duration-500"
            style={{ width: `${checkpoint.progress || 0}%` }} // 0% default
          />
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {checkpoint.progress ? `${checkpoint.progress}% done` : "0% done"}
        </p>
      </div>
    </div>
  );
};

export default Roadmap;
